<script lang="ts">
  /**
   * Catalog shell: tool backends (dump ∩ filter) then Production · HF · CF · CivitAI · endpoints
   */
  import { onMount } from "svelte";
  import type { GpuPluginHost } from "../host.js";
  import { hostVisibleCatalogTabs } from "../host.js";
  import type { GpuNavTarget } from "../types.js";
  import type { Connection, ConnectionTab } from "../connections.js";
  import {
    fetchCatalogRuntime,
    fetchCatalogTools,
    loadToolBackends,
    PRIMARY_TOOL,
    saveToolBackend,
    toolNeedsBackend,
    type CatalogBoardTool,
    type ToolBackend,
  } from "../catalog.js";
  import CatalogBrowser from "./CatalogBrowser.svelte";
  import HfBrowser from "./HfBrowser.svelte";
  import CloudflareBrowser from "./CloudflareBrowser.svelte";
  import CivitaiBrowser from "./CivitaiBrowser.svelte";
  import ForgeEndpointBrowser from "./ForgeEndpointBrowser.svelte";

  const ALL_TABS: { id: ConnectionTab; label: string; variant?: string }[] = [
    { id: "production", label: "Production", variant: "sky" },
    { id: "hf", label: "HF models" },
    { id: "cloudflare", label: "Cloudflare", variant: "inference" },
    { id: "civitai", label: "CivitAI", variant: "amber" },
    { id: "forge_endpoint", label: "Forge endpoints", variant: "sky" },
  ];

  interface Props {
    host: GpuPluginHost;
    tab?: ConnectionTab;
    defaultTab?: ConnectionTab;
    onTabChange?: (tab: ConnectionTab) => void;
    showTabBar?: boolean;
  }

  let {
    host,
    tab,
    defaultTab = "cloudflare",
    onTabChange,
    showTabBar = true,
  }: Props = $props();

  function hostStartTool(h: GpuPluginHost): string {
    const app = (h.appId || "").trim();
    return (h.accessHubTool || (app ? PRIMARY_TOOL[app] : "") || "").trim();
  }

  let internal = $state<ConnectionTab>("cloudflare");
  let tools = $state<CatalogBoardTool[]>([]);
  let backends = $state<Record<string, ToolBackend>>({});
  let activeTool = $state(hostStartTool(host));
  let selectorOn = $state(true);

  const appId = $derived((host.appId || "").trim());
  const modelTools = $derived(tools.filter((t) => toolNeedsBackend(t)));

  const visible = $derived(
    ALL_TABS.filter((t) => hostVisibleCatalogTabs(host).includes(t.id)),
  );

  function houseLabel(t: CatalogBoardTool): string {
    const you = backends[t.tool_id];
    if (you?.name) return you.name;
    return t.dump_name || t.dump_id || "Pick";
  }

  function applyToolDefault(toolId: string, fromSelectorOff = false) {
    const you = backends[toolId];
    if (you?.modelId) {
      host.onAccessHubModel?.(you.modelId, {
        name: you.name,
        dumpId: you.dumpId,
        toolId,
        selector: true,
      });
      return;
    }
    const app = appId;
    if (!app || !toolId) return;
    void fetchCatalogRuntime(app, toolId, { base: host.dumpCatalogUrl })
      .then((rt) => {
        const modelId = rt.dump?.modelId;
        if (!modelId || !rt.enabled) return;
        if (rt.model_selector && fromSelectorOff) return;
        host.onAccessHubModel?.(modelId, {
          name: rt.dump?.name,
          dumpId: rt.dump?.id || rt.dump_id || undefined,
          toolId,
          selector: rt.model_selector,
        });
      })
      .catch(() => {
        /* dump empty or catalog down — picker still works */
      });
  }

  function setTool(id: string) {
    activeTool = id;
    applyToolDefault(id);
  }

  onMount(() => {
    if (tab === undefined) {
      const allowed = visible.map((t) => t.id);
      internal = allowed.includes(defaultTab) ? defaultTab : allowed[0] || "cloudflare";
    }
    const app = host.appId;
    const start = hostStartTool(host);
    activeTool = start;
    if (app) backends = loadToolBackends(app);
    if (!app) return;
    void fetchCatalogTools(app, { base: host.dumpCatalogUrl })
      .then((board) => {
        tools = board.tools || [];
        selectorOn = board.model_selector !== false;
        const pickable = (board.tools || []).filter((t) => toolNeedsBackend(t));
        if (start && pickable.some((t) => t.tool_id === start)) {
          activeTool = start;
        } else if (pickable[0]) {
          activeTool = pickable[0].tool_id;
        }
        applyToolDefault(activeTool, true);
      })
      .catch(() => {
        applyToolDefault(start, true);
      });
  });

  const active = $derived(tab ?? internal);

  function setTab(next: ConnectionTab) {
    internal = next;
    onTabChange?.(next);
  }

  const wrappedHost = $derived.by((): GpuPluginHost => ({
    ...host,
    accessHubTool: activeTool || host.accessHubTool,
    onConnect: async (conn: Connection) => {
      const toolId = conn.toolId || activeTool || host.accessHubTool;
      const dumpId = conn.dumpId || conn.skuId || conn.id;
      const next: Connection = { ...conn, toolId, dumpId };
      if (appId && toolId && dumpId) {
        backends = saveToolBackend(appId, {
          toolId,
          dumpId,
          modelId: String(conn.modelId || conn.sourceId || ""),
          name: conn.label,
        });
      }
      await host.onConnect?.(next);
    },
    onNavigate: (target: GpuNavTarget) => {
      const tabs: ConnectionTab[] = ["production", "hf", "cloudflare", "civitai", "forge_endpoint"];
      if ((tabs as string[]).includes(target)) setTab(target as ConnectionTab);
      host.onNavigate?.(target);
    },
  }));
</script>

<div class="fgp-root fgp-shell">
  {#if selectorOn && modelTools.length > 0}
    <div class="fgp-tool-list" role="listbox" aria-label="Tool backends">
      {#each modelTools as t}
        <button
          type="button"
          class="fgp-tool"
          role="option"
          aria-selected={activeTool === t.tool_id}
          data-active={activeTool === t.tool_id}
          title={`${t.label} · ${houseLabel(t)}`}
          onclick={() => setTool(t.tool_id)}
        >
          <span class="fgp-tool-name">{t.label}</span>
          <span class="fgp-tool-be">{houseLabel(t)}</span>
        </button>
      {/each}
    </div>
  {/if}
  {#if showTabBar}
    <div class="fgp-tabs" role="tablist" aria-label="Forge connections">
      {#each visible as t}
        <button
          type="button"
          class="fgp-tab"
          role="tab"
          data-variant={t.variant}
          data-active={active === t.id}
          aria-selected={active === t.id}
          onclick={() => setTab(t.id)}
        >
          {t.label}
        </button>
      {/each}
    </div>
  {/if}

  <div class="fgp-tab-panel" role="tabpanel">
    {#key `${active}:${activeTool}`}
      {#if active === "production"}
        <CatalogBrowser host={wrappedHost} tab="production" />
      {:else if active === "hf"}
        <HfBrowser host={wrappedHost} />
      {:else if active === "civitai"}
        <CivitaiBrowser host={wrappedHost} />
      {:else if active === "forge_endpoint"}
        <ForgeEndpointBrowser host={wrappedHost} />
      {:else}
        <CloudflareBrowser host={wrappedHost} />
      {/if}
    {/key}
  </div>
</div>
